import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  makeStyles,
  CircularProgress,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {
  GET_EMAIL_TEMPLATE,
  GetEmailTemplateResponse,
} from '../../core/graphql/queries/emailTemplates';
import {
  UPDATE_EMAIL_TEMPLATE,
  SEND_EMAIL_TEMPLATE_TEST,
  SendEmailTemplateTestResponse,
  SendEmailTemplateTestVariables,
  UpdateEmailTemplateResponse,
  UpdateEmailTemplateVariables,
} from '../../core/graphql/mutations/emailTemplates';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#02172a',
    minHeight: '100vh',
    padding: theme.spacing(3),
  },
  title: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1.75rem',
    fontFamily: '"Syne", "Space Grotesk", sans-serif',
  },
  meta: {
    color: '#8899aa',
    fontSize: '0.85rem',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  statCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    height: '100%',
  },
  statNumber: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '1.75rem',
  },
  statLabel: {
    color: '#b0b0b0',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  field: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#02172a',
      color: '#fff',
      '& fieldset': { borderColor: '#2a3a4a' },
    },
    '& .MuiInputLabel-root': { color: '#b0b0b0' },
    '& .MuiFormHelperText-root': { color: '#8899aa' },
  },
  bodyField: {
    '& textarea': {
      fontFamily: '"JetBrains Mono", "SF Mono", Consolas, monospace',
      fontSize: '0.85rem',
    },
  },
  editorShell: {
    border: '1px solid #2a3a4a',
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
    backgroundColor: '#02172a',
  },
  visualToolbar: {
    borderBottom: '1px solid #2a3a4a',
    backgroundColor: '#0a1f34',
    padding: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
    '& button': {
      color: '#e8e8e8',
      border: '1px solid #2a3a4a',
      background: 'transparent',
      borderRadius: 4,
      padding: '4px 8px',
      cursor: 'pointer',
    },
  },
  visualEditor: {
    minHeight: 260,
    padding: theme.spacing(2),
    color: '#fff',
    fontSize: '0.95rem',
    outline: 'none',
    '& a': {
      color: '#90caf9',
    },
  },
  modeTabs: {
    marginBottom: theme.spacing(1.5),
    borderBottom: '1px solid #2a3a4a',
  },
  previewCard: {
    backgroundColor: '#0a1f34',
    border: '1px solid #2a3a4a',
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    color: '#e8e8e8',
    padding: theme.spacing(2),
  },
  dirtyChip: {
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    padding: '2px 10px',
    borderRadius: 999,
    backgroundColor: 'rgba(255, 152, 0, 0.15)',
    color: '#ffb74d',
    fontSize: '0.75rem',
    fontWeight: 600,
    verticalAlign: 'middle',
  },
}));

function pct(num: number, den: number): string {
  if (!den) return '0%';
  return `${Math.round((num / den) * 1000) / 10}%`;
}

function formatLastSent(ts: number | null | undefined): string {
  if (ts == null) return 'Never';
  const d = new Date(ts);
  const days = Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Last sent: today';
  if (days === 1) return 'Last sent: 1 day ago';
  return `Last sent: ${days} days ago`;
}

function sanitizeForPreview(html: string): string {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '');
}

const AdminEmailTemplateDetail: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useQuery<GetEmailTemplateResponse>(GET_EMAIL_TEMPLATE, {
    variables: { id },
    skip: !id,
  });

  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bodyMode, setBodyMode] = useState<'visual' | 'html'>('visual');
  const visualEditorRef = useRef<HTMLDivElement | null>(null);
  const [baseline, setBaseline] = useState({ name: '', purpose: '', subject: '', body: '' });
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [showTestForm, setShowTestForm] = useState(false);
  const [testToEmail, setTestToEmail] = useState('');

  const [updateTemplate, { loading: saving }] = useMutation<
    UpdateEmailTemplateResponse,
    UpdateEmailTemplateVariables
  >(UPDATE_EMAIL_TEMPLATE, {
    refetchQueries: [{ query: GET_EMAIL_TEMPLATE, variables: { id } }],
  });
  const [sendTemplateTest, { loading: testSending }] = useMutation<
    SendEmailTemplateTestResponse,
    SendEmailTemplateTestVariables
  >(SEND_EMAIL_TEMPLATE_TEST);

  const tmpl = data?.getEmailTemplate;

  useEffect(() => {
    if (!tmpl) return;
    const next = {
      name: tmpl.name,
      purpose: tmpl.purpose,
      subject: tmpl.subject,
      body: tmpl.body,
    };
    setName(next.name);
    setPurpose(next.purpose);
    setSubject(next.subject);
    setBody(next.body);
    setBaseline(next);
  }, [tmpl]);

  const dirty = useMemo(() => {
    return (
      name !== baseline.name ||
      purpose !== baseline.purpose ||
      subject !== baseline.subject ||
      body !== baseline.body
    );
  }, [name, purpose, subject, body, baseline]);
  const sanitizedPreview = useMemo(() => sanitizeForPreview(body || ''), [body]);

  useEffect(() => {
    if (bodyMode !== 'visual' || !visualEditorRef.current) return;
    if (visualEditorRef.current.innerHTML !== body) {
      visualEditorRef.current.innerHTML = body || '';
    }
  }, [body, bodyMode]);

  const runVisualCommand = (command: string) => {
    document.execCommand(command, false);
    const next = visualEditorRef.current?.innerHTML ?? '';
    setBody(next);
  };

  const handleSave = async () => {
    try {
      await updateTemplate({
        variables: {
          id,
          input: { name, purpose, subject, body },
        },
      });
      setBaseline({ name, purpose, subject, body });
      setSnack({ open: true, message: 'Template saved.', severity: 'success' });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed.';
      setSnack({
        open: true,
        message: msg,
        severity: 'error',
      });
    }
  };

  const handleSendTestEmail = async () => {
    const to = testToEmail.trim();
    if (!to) {
      setSnack({
        open: true,
        message: 'Please enter a recipient email.',
        severity: 'error',
      });
      return;
    }

    try {
      const res = await sendTemplateTest({
        variables: { id, to },
      });
      const payload = res.data?.sendEmailTemplateTest;
      if (!payload?.success) {
        setSnack({
          open: true,
          message: payload?.message || 'Failed to send test email.',
          severity: 'error',
        });
        return;
      }
      setSnack({
        open: true,
        message: payload.message || 'Test email sent.',
        severity: 'success',
      });
      setShowTestForm(false);
      setTestToEmail('');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to send test email.';
      setSnack({
        open: true,
        message: msg,
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box className={classes.root} display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress style={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (error || !tmpl) {
    return (
      <Box className={classes.root}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/admin/emails')} style={{ color: '#90caf9' }}>
          Back
        </Button>
        <Typography className={classes.title} style={{ marginTop: 16 }}>
          Template not found
        </Typography>
        <Typography style={{ color: '#b0b0b0' }}>{error?.message}</Typography>
      </Box>
    );
  }

  const st = tmpl.stats;
  const sent = st?.sentCount ?? 0;

  return (
    <Box className={classes.root}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/admin/emails')} style={{ color: '#90caf9' }}>
        All templates
      </Button>

      <Box display="flex" alignItems="center" flexWrap="wrap" mt={2}>
        <Typography className={classes.title}>{tmpl.name}</Typography>
        {dirty && <span className={classes.dirtyChip}>Unsaved changes</span>}
      </Box>
      <Typography className={classes.meta}>
        Trigger: <strong style={{ color: '#e1bee7' }}>{tmpl.triggerEvent}</strong>
        {tmpl.isActive === false && ' · inactive'}
      </Typography>
      <Typography className={classes.meta}>{formatLastSent(st?.lastSentAt)}</Typography>

      {tmpl.triggerEvent.startsWith('external.') && (
        <Alert severity="info" style={{ marginBottom: 16 }}>
          This template is a <strong>registry entry</strong> for email sent by a third party (Firebase or Stripe), not
          through the StarFarm GraphQL/Mailgun pipeline. Delivery stats usually stay at zero. Edit live wording in the
          provider dashboard (see Purpose). Saving here keeps internal copy aligned for your team.
        </Alert>
      )}
      {tmpl.triggerEvent.startsWith('mailgun.template.') && (
        <Alert severity="info" style={{ marginBottom: 16 }}>
          This row is mapped to a Mailgun stored template. The test-send button sends using the Mailgun template key{' '}
          <strong>{tmpl.triggerEvent.replace('mailgun.template.', '')}</strong>. For full visual/template changes,
          edit it in Mailgun Templates and keep this dashboard row for cataloging and test sends.
        </Alert>
      )}

      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard} elevation={0}>
            <CardContent>
              <Typography className={classes.statLabel}>Total sent</Typography>
              <Typography className={classes.statNumber}>{sent}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard} elevation={0}>
            <CardContent>
              <Typography className={classes.statLabel}>Open rate</Typography>
              <Typography className={classes.statNumber}>{pct(st?.openCount ?? 0, sent)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard} elevation={0}>
            <CardContent>
              <Typography className={classes.statLabel}>Click rate</Typography>
              <Typography className={classes.statNumber}>{pct(st?.clickCount ?? 0, sent)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.statCard} elevation={0}>
            <CardContent>
              <Typography className={classes.statLabel}>Bounce rate</Typography>
              <Typography className={classes.statNumber}>{pct(st?.bounceCount ?? 0, sent)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TextField
        className={classes.field}
        label="Name"
        fullWidth
        variant="outlined"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextField
        className={classes.field}
        label="Purpose"
        fullWidth
        variant="outlined"
        multiline
        minRows={2}
        value={purpose}
        onChange={(e) => setPurpose(e.target.value)}
      />
      <TextField
        className={classes.field}
        label="Subject line"
        fullWidth
        variant="outlined"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      />
      <Paper elevation={0} className={classes.modeTabs}>
        <Tabs
          value={bodyMode}
          onChange={(_: unknown, next: 'visual' | 'html') => setBodyMode(next)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value="visual" label="Visual editor" />
          <Tab value="html" label="HTML source" />
        </Tabs>
      </Paper>
      {bodyMode === 'visual' ? (
        <div className={classes.editorShell}>
          <div className={classes.visualToolbar}>
            <button type="button" onClick={() => runVisualCommand('bold')}>
              Bold
            </button>
            <button type="button" onClick={() => runVisualCommand('italic')}>
              Italic
            </button>
            <button type="button" onClick={() => runVisualCommand('underline')}>
              Underline
            </button>
            <button type="button" onClick={() => runVisualCommand('insertUnorderedList')}>
              Bullets
            </button>
          </div>
          <div
            ref={visualEditorRef}
            className={classes.visualEditor}
            contentEditable
            suppressContentEditableWarning
            onInput={(e) => setBody((e.currentTarget as HTMLDivElement).innerHTML)}
          />
        </div>
      ) : (
        <TextField
          className={`${classes.field} ${classes.bodyField}`}
          label="Body HTML source"
          fullWidth
          variant="outlined"
          multiline
          minRows={16}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      )}
      <Paper elevation={0} className={classes.previewCard}>
        <Typography variant="subtitle2" style={{ color: '#b0b0b0', marginBottom: 12 }}>
          Live preview (sanitized)
        </Typography>
        <div dangerouslySetInnerHTML={{ __html: sanitizedPreview }} />
      </Paper>
      <Typography className={classes.meta} style={{ marginTop: 0 }}>
        Use {'{{variableName}}'} for merge fields your backend supplies when sending.
      </Typography>

      <Box mt={2}>
        <Button
          variant="contained"
          color="primary"
          disabled={!dirty || saving}
          onClick={() => void handleSave()}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        <Button
          variant="outlined"
          style={{ marginLeft: 12, color: '#90caf9', borderColor: '#2a3a4a' }}
          onClick={() => setShowTestForm((v) => !v)}
        >
          {showTestForm ? 'Cancel test email' : 'Send test email'}
        </Button>
      </Box>

      {showTestForm && (
        <Box mt={2} display="flex" alignItems="center" flexWrap="wrap" style={{ columnGap: 12, rowGap: 12 }}>
          <TextField
            className={classes.field}
            style={{ minWidth: 320, marginBottom: 0 }}
            label="To email"
            variant="outlined"
            value={testToEmail}
            onChange={(e) => setTestToEmail(e.target.value)}
            placeholder="name@example.com"
          />
          <Button
            variant="contained"
            color="secondary"
            disabled={testSending}
            onClick={() => void handleSendTestEmail()}
          >
            {testSending ? 'Sending…' : 'Send now'}
          </Button>
        </Box>
      )}

      <Box mt={1}>
        <Typography className={classes.meta}>
          Test email sends this template to your chosen recipient using Mailgun with sample placeholder values.
        </Typography>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminEmailTemplateDetail;
